/*
 * ThemeContext — single source of truth for the SAMURAI theme.
 *
 * Values: 'dark' (default) | 'light'
 * Precedence: user's saved localStorage choice → OS `prefers-color-scheme` → 'dark'.
 * Applies `data-theme='light'|'dark'` on <html> so the CSS variables in
 * index.css cascade through every component without any per-component prop.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "samurai_theme";

function resolveInitialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

function applyThemeAttr(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => resolveInitialTheme());

  // Sync attribute + persist on every change.
  useEffect(() => {
    applyThemeAttr(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {}
  }, [theme]);

  // Set on mount ASAP (before first paint) — resolve function runs at state init.
  useEffect(() => {
    applyThemeAttr(theme);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Respond to system preference changes only while the user hasn't chosen manually.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return; // user has an explicit preference; leave it alone
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setTheme(e.matches ? "light" : "dark");
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      // brief transition class so every child element eases between themes
      const root = document.documentElement;
      root.classList.add("theme-transitioning");
      window.setTimeout(() => root.classList.remove("theme-transitioning"), 400);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggle, isDark: theme === "dark" }),
    [theme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
